<template>
    <form
        v-if="renderable && !submitted"
        ref="form-element"
        novalidate
        class="storyblok-form"
        :aria-label="definition.title"
        :aria-busy="isSubmitting || undefined"
        @submit.prevent="submitForm"
    >
        <label
            class="k-visually-hidden"
            aria-hidden="true"
        >
            Website
            <input
                v-model="website"
                name="website"
                tabindex="-1"
                autocomplete="off"
            >
        </label>
        <h2
            v-if="!hideTitle"
            class="storyblok-form__title"
        >
            {{ definition.title }}
        </h2>

        <div class="storyblok-form__fields">
            <form.Field
                v-for="field in fields"
                :key="field.uid"
                v-slot="{ field: formField }"
                :name="field.name"
                :validators="{
                    onDynamic: ({ value }) => validateDynamicField(field, value),
                }"
            >
                <KField
                    class="storyblok-form__field"
                    :class="{ 'storyblok-form__field--wide': field.component === 'textarea-field' }"
                    :label="field.label"
                    :required="field.required"
                    :error="firstError(formField.state.meta.errors)"
                >
                    <KInput
                        v-if="field.component === 'input-field'"
                        :type="field.type"
                        :name="field.name"
                        :placeholder="field.placeholder"
                        :autocomplete="autocompleteFor(field)"
                        :model-value="formField.state.value"
                        @blur="formField.handleBlur"
                        @update:model-value="value => formField.handleChange(String(value))"
                    />

                    <KTextarea
                        v-else-if="field.component === 'textarea-field'"
                        :name="field.name"
                        :placeholder="field.placeholder"
                        :model-value="formField.state.value"
                        @blur="formField.handleBlur"
                        @update:model-value="formField.handleChange"
                    />

                    <KDatePicker
                        v-else-if="field.component === 'date-field'"
                        :name="field.name"
                        :calendar-label="field.label"
                        :model-value="toCalendarDate(formField.state.value)"
                        @update:model-value="value => formField.handleChange(value ? value.toString() : '')"
                    />

                    <KSelect
                        v-else
                        :name="field.name"
                        :options="field.options"
                        :placeholder="field.placeholder || 'Choose an option'"
                        :model-value="formField.state.value"
                        @blur="formField.handleBlur"
                        @update:model-value="value => formField.handleChange(String(value ?? ''))"
                    />
                </KField>
            </form.Field>
        </div>

        <KButton
            type="submit"
            full-width
            :disabled="isSubmitting"
            :label="isSubmitting ? 'Sending...' : 'Submit'"
        />

        <p
            v-if="serverError"
            class="storyblok-form__error"
            role="alert"
        >
            Something went wrong. Please try again.
        </p>
    </form>

    <div
        v-else-if="renderable"
        class="storyblok-form__success"
    >
        <Icon
            class="storyblok-form__icon"
            name="tabler:check-circle"
        />
        <p
            class="storyblok-form__success-message u-header-3"
            role="status"
        >
            {{ definition.success_message }}
        </p>
    </div>
</template>

<script setup lang="ts">
import type { CalendarDate } from '@internationalized/date'
import type { RequestSubmission } from '@vemedia/request-contract'
import type { StoryblokFormDefinition } from '~/utils/storyblok-forms'
import { parseDate } from '@internationalized/date'
import { revalidateLogic, useForm } from '@tanstack/vue-form'
import { KButton, KDatePicker, KField, KInput, KSelect, KTextarea } from '@vemedia/karibu'
import { nextTick, ref, useTemplateRef } from 'vue'
import {
    buildDynamicFormRequest,
    createDynamicDefaults,
    normalizeFormFields,
    validateDynamicField,
} from '~/utils/storyblok-forms'

const { definition, formId, send, hideTitle = false } = defineProps<{
    definition: StoryblokFormDefinition
    formId: string
    send: (payload: RequestSubmission) => Promise<void>
    hideTitle?: boolean
}>()

const emit = defineEmits<{
    submitted: [payload: RequestSubmission]
    submitError: []
    invalid: []
}>()

// The CMS schema is an immutable snapshot for one mount. A Visual Editor schema change
// must remount this component (the consumers key it on the field definitions) so TanStack
// receives a complete new default-value shape. An unrenderable definition fails at this
// single boundary: the form is withheld instead of silently mangling endpoint data.
const fields = (() => {
    try {
        return normalizeFormFields(definition.fields ?? [])
    } catch (error) {
        console.error('[storyblok-form] Withholding unrenderable Storyblok form:', error)
        return []
    }
})()
const renderable = fields.length > 0

const formElement = useTemplateRef<HTMLFormElement>('form-element')
const serverError = ref(false)
const submitted = ref(false)
const website = ref('')

const form = useForm({
    defaultValues: createDynamicDefaults(fields),
    validationLogic: revalidateLogic({
        mode: 'blur',
        modeAfterSubmission: 'change',
    }),
    onSubmitInvalid: () => {
        emit('invalid')
        void nextTick(() => {
            formElement.value
                ?.querySelector<HTMLElement>('[aria-invalid="true"]')
                ?.focus()
        })
    },
    onSubmit: async ({ value }) => {
        const payload = buildDynamicFormRequest(value, fields, definition.title, formId, window.location.href, website.value)

        await send(payload)
        submitted.value = true
        emit('submitted', payload)
    },
})

const isSubmitting = form.useSelector(state => state.isSubmitting)

function firstError(errors: unknown[]) {
    return errors.find(error => typeof error === 'string') as string | undefined
}

// The form state stores dates as the ISO `YYYY-MM-DD` string KDatePicker's hidden input
// submits; the picker itself models an immutable CalendarDate. An empty or malformed value
// leaves the picker blank rather than throwing during hydration.
function toCalendarDate(value: string): CalendarDate | undefined {
    if (!value) {
        return undefined
    }
    try {
        return parseDate(value)
    } catch {
        return undefined
    }
}

function autocompleteFor(field: (typeof fields)[number]) {
    if (field.component !== 'input-field') {
        return undefined
    }
    if (field.type === 'email' || field.type === 'tel') {
        return field.type
    }
    if (field.name === 'fullname' || field.name === 'name') {
        return 'name'
    }
}

async function submitForm() {
    // TanStack 1.33.2 does not deduplicate direct `handleSubmit()` calls, and a finished
    // form must not be resent; server errors leave the form retryable.
    if (isSubmitting.value || submitted.value) {
        return
    }

    serverError.value = false

    try {
        await form.handleSubmit()
    } catch {
        serverError.value = true
        emit('submitError')
    }
}
</script>

<style lang="scss">
@use 'storyblok-form';
</style>
